import { useRef, useState } from 'react'

import { ConnectError } from '@connectrpc/connect'
import {
  type SplitSupplyNoticeErrorReason,
  SplitSupplyNoticeErrorReasonSchema,
  type SplitSupplyNoticeSection,
} from '@ebalance/proto/bffuser/v1/split_supply_plan_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Flex,
  HStack,
  PrimaryButton,
  TertiaryButton,
  Text,
  UploadArea,
} from '@enechain/ecloud-designsystem'

import { Modal } from '~/components/common/Modal'
import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { readAppErrorInfo } from '~/libs/appErrorInfo'
import { NoticeImportFileTooLargeError } from '~/pages/SplitSupplyPlan/hooks/uploadNoticeImportFile'
import {
  MAX_IMPORT_FILES,
  type SelectedImportFile,
  canConfirmFiles,
  uploadedTokens,
  withoutFilesNamed,
} from '~/pages/SplitSupplyPlan/models/noticeImport'
import {
  SECTION_OF_TAB,
  type SectionTab,
} from '~/pages/SplitSupplyPlan/models/period'
import { describeSplitSupplyNoticeError } from '~/pages/SplitSupplyPlan/models/serverError'

/** 確認 (preview) の response のうち、 取込ダイアログが見る部分 (他社の取込と自社の取込で共通)。 */
export type NoticeImportPreview = {
  digest: string
  errorCount: number
  errors: ReadonlyArray<{ fileName: string }>
  tooLarge: boolean
  nothing: boolean
  notices: readonly unknown[]
}

/** 取込の側 (他社の通告値 / 自社の通告値) ごとに違うこと。 ダイアログの流れ自体は同じ。 */
export type NoticeImportSide<R extends NoticeImportPreview> = {
  /** 文言の置き場 (例 `page:splitSupplyPlan.own.import`)。 */
  prefix: string
  /** ファイル 1 つを上げて取込の token を返す。 */
  uploadFile: (file: File) => Promise<string>
  /** 確認する。 token は使い切らない。 */
  preview: (input: {
    uploadTokens: string[]
    section: SplitSupplyNoticeSection
  }) => Promise<R>
  /**
   * 取り込み、 成功のトーストの題 (と説明) を返す。 トーストの代わりに結果を別に出した時
   * (他社の XML でスキップがあった時のエラーバナー) は undefined を返す。
   */
  runImport: (input: {
    uploadTokens: string[]
    section: SplitSupplyNoticeSection
    digest: string
  }) => Promise<{ title: string; description?: string } | undefined>
  isPreviewing: boolean
  isImporting: boolean
  /** 選んだファイルでは続けられない reason (内容が拒まれた・ファイルの受け取りが切れた)。 */
  restartReasons: ReadonlySet<SplitSupplyNoticeErrorReason>
  /** 確認の後に状況が変わった reason (選んだファイルのまま確認し直す)。 */
  changedReason: SplitSupplyNoticeErrorReason
  /** 確認画面 (ダイアログの 2 段目)。 */
  renderConfirmation: (props: {
    result: R
    onRemoveBadFiles: (() => void) | undefined
  }) => React.ReactElement
  /** ファイル選択 (1 段目) の説明の下に出すもの (自社のテンプレートのボタン)。 */
  selectExtra?: React.ReactNode
}

type Props<R extends NoticeImportPreview> = {
  isOpen: boolean
  onClose: () => void
  /** 開いている断面 (ファイルはこの断面のものを選ぶ)。 */
  tab: SectionTab
  side: NoticeImportSide<R>
}

type Step = 'select' | 'confirm'

/**
 * 通告値の取込のダイアログ (他社の通告値・自社の通告値で共通)。 1 段目でファイル (XML・ZIP・CSV、 複数) を
 * 選ぶと、 選んだ時に 1 つずつ受け取り先へ上げる。 「確認」 で 2 段目 (確認画面) に進み、 取り込む内容を
 * 見て 「取り込む」。 取り込む内容は確認画面に出たものと必ず同じで、 確認の後に状況が変わっていたら何も
 * 取り込まず、 選んだファイルのまま確認し直す。 誤りが 1 つでもあれば取り込めない (誤りのあるファイルだけ
 * 外して確認し直せる)。 読み取りと判定は backend が持つ。
 */
export const NoticeImportDialog = <R extends NoticeImportPreview>({
  isOpen,
  onClose,
  tab,
  side,
}: Props<R>): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccess = useSuccessToast()
  const showError = useErrorToast()
  const text = (key: string, options?: Record<string, unknown>): string =>
    t(`${side.prefix}.${key}`, options)

  const [files, setFiles] = useState<SelectedImportFile[]>([])
  const [step, setStep] = useState<Step>('select')
  const [result, setResult] = useState<R | undefined>(undefined)
  // 1 段目・2 段目の上に出す案内 (期限切れ・確認の後に変わった・ファイルが多すぎる)
  const [notice, setNotice] = useState<string | undefined>(undefined)
  const nextId = useRef(0)
  const section = SECTION_OF_TAB[tab]
  const isBusy = side.isPreviewing || side.isImporting

  const addFiles = (picked: File[]): void => {
    const room = MAX_IMPORT_FILES - files.length
    const chosen = picked.slice(0, Math.max(room, 0))
    setNotice(picked.length > chosen.length ? text('tooManyFiles') : undefined)
    for (const file of chosen) {
      const id = nextId.current++
      setFiles((prev) => [
        ...prev,
        { id, name: file.name, status: 'uploading' },
      ])
      void side
        .uploadFile(file)
        .then((token) => {
          setFiles((prev) =>
            prev.map((f) =>
              f.id === id
                ? { id, name: file.name, status: 'uploaded', token }
                : f,
            ),
          )
        })
        .catch((err: unknown) => {
          setFiles((prev) =>
            prev.map((f) =>
              f.id === id
                ? {
                    id,
                    name: file.name,
                    status: 'failed',
                    tooLarge: err instanceof NoticeImportFileTooLargeError,
                  }
                : f,
            ),
          )
        })
    }
  }

  const removeFile = (id: number): void => {
    setFiles((prev) => prev.filter((f) => f.id !== id))
  }

  /** 選んだファイルでは続けられない時の、 ファイル選択へ戻る (ファイルは選び直す)。 */
  const restart = (message: string): void => {
    setFiles([])
    setResult(undefined)
    setStep('select')
    setNotice(message)
  }

  const reasonOf = (err: unknown): SplitSupplyNoticeErrorReason | undefined =>
    err instanceof ConnectError
      ? readAppErrorInfo(err, SplitSupplyNoticeErrorReasonSchema)?.reason
      : undefined

  /** `selected` のファイルを確認する。 確認できたら 2 段目へ。 */
  const confirm = async (selected: SelectedImportFile[]): Promise<boolean> => {
    try {
      const res = await side.preview({
        uploadTokens: uploadedTokens(selected),
        section,
      })
      setResult(res)
      setStep('confirm')
      return true
    } catch (err) {
      const reason = reasonOf(err)
      if (reason !== undefined && side.restartReasons.has(reason)) {
        restart(describeSplitSupplyNoticeError(err, t))
      } else {
        showError({
          title: text('previewFailedTitle'),
          description: describeSplitSupplyNoticeError(err, t),
        })
      }
      return false
    }
  }

  const handleConfirm = (): void => {
    setNotice(undefined)
    void confirm(files)
  }

  const handleSubmit = async (): Promise<void> => {
    if (result === undefined) return
    setNotice(undefined)
    try {
      const outcome = await side.runImport({
        uploadTokens: uploadedTokens(files),
        section,
        digest: result.digest,
      })
      if (outcome !== undefined) showSuccess(outcome)
      onClose()
    } catch (err) {
      const reason = reasonOf(err)
      if (reason === side.changedReason) {
        // 確認の後に状況が変わった: token は使い切っていないので、 選んだファイルのまま確認し直す
        const changed = describeSplitSupplyNoticeError(err, t)
        if (await confirm(files)) setNotice(changed)
      } else if (reason !== undefined && side.restartReasons.has(reason)) {
        restart(describeSplitSupplyNoticeError(err, t))
      } else {
        showError({
          title: text('errorTitle'),
          description: describeSplitSupplyNoticeError(err, t),
        })
      }
    }
  }

  // 誤りのファイルのうち、 選んだファイルの名前と一致するものを外した残り (残りがあり、 外すものがある時だけ)
  const errorFileNames = new Set(
    (result?.errors ?? []).map((e) => e.fileName).filter((n) => n !== ''),
  )
  const remaining = withoutFilesNamed(files, errorFileNames)
  const canRemoveBad = remaining.length > 0 && remaining.length < files.length

  const handleRemoveBad = (): void => {
    setFiles(remaining)
    setNotice(undefined)
    void confirm(remaining)
  }

  const canSubmit =
    result !== undefined &&
    !result.tooLarge &&
    !result.nothing &&
    result.errorCount === 0 &&
    result.notices.length > 0 &&
    !isBusy

  const footer =
    step === 'select' ? (
      <HStack justify="flex-end" gap={2} width="100%">
        <TertiaryButton onClick={onClose} size="md">
          {t('common:action.cancel')}
        </TertiaryButton>
        <PrimaryButton
          disabled={!canConfirmFiles(files) || isBusy}
          loading={side.isPreviewing}
          onClick={handleConfirm}
          size="md"
        >
          {text('confirm')}
        </PrimaryButton>
      </HStack>
    ) : (
      <HStack justify="flex-end" gap={2} width="100%">
        <TertiaryButton
          disabled={isBusy}
          onClick={() => {
            setNotice(undefined)
            setStep('select')
          }}
          size="md"
        >
          {text('back')}
        </TertiaryButton>
        <PrimaryButton
          disabled={!canSubmit}
          loading={side.isImporting}
          onClick={() => {
            void handleSubmit()
          }}
          size="md"
        >
          {text('submit')}
        </PrimaryButton>
      </HStack>
    )

  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      size={step === 'confirm' ? '2xl' : 'md'}
      title={text('title')}
    >
      <Flex flexDirection="column" gap={4}>
        {notice !== undefined && (
          <Alert description={notice} status="warning" />
        )}
        {step === 'select' ? (
          <>
            <Text textStyle="caption" color="Text.midEmphasis">
              {text('note')}
            </Text>
            {side.selectExtra}
            <Flex flexDirection="column" gap={2}>
              <UploadArea
                accept=".xml,.zip,.csv"
                multiple
                onSelectFiles={(event, list) => {
                  // 同じファイルを続けて選んでも change が発火するよう空に戻す
                  const picked = Array.from(list)
                  if (event.target instanceof HTMLInputElement) {
                    event.target.value = ''
                  }
                  addFiles(picked)
                }}
              />
              <Text color="Text.lowEmphasis" fontSize="md" textAlign="center">
                {text('acceptedFormats')}
              </Text>
            </Flex>
            {files.length > 0 && (
              <Flex flexDirection="column" gap={1}>
                <Text textStyle="bodyBold">{text('selectedFiles')}</Text>
                {files.map((f) => (
                  <Flex key={f.id} alignItems="center" gap={3}>
                    <Text flex="1" wordBreak="break-all">
                      {f.name}
                    </Text>
                    <Text
                      textStyle="caption"
                      color={
                        f.status === 'failed'
                          ? 'Text.negativeMid'
                          : 'Text.midEmphasis'
                      }
                    >
                      {f.status === 'failed'
                        ? text(
                            f.tooLarge
                              ? 'fileStatus.tooLarge'
                              : 'fileStatus.failed',
                          )
                        : text(`fileStatus.${f.status}`)}
                    </Text>
                    <TertiaryButton
                      aria-label={text('removeFileLabel', { name: f.name })}
                      onClick={() => {
                        removeFile(f.id)
                      }}
                      size="sm"
                    >
                      {text('removeFile')}
                    </TertiaryButton>
                  </Flex>
                ))}
              </Flex>
            )}
          </>
        ) : (
          result !== undefined &&
          side.renderConfirmation({
            result,
            onRemoveBadFiles: canRemoveBad ? handleRemoveBad : undefined,
          })
        )}
      </Flex>
    </Modal>
  )
}
